import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
  Skeleton,
  Link,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EuroIcon from '@mui/icons-material/Euro';
import MapIcon from '@mui/icons-material/Map';
import { useNavigate } from 'react-router-dom';
import { ListingStatusChip, PermissionChip } from './StatusChips';
import { FavoriteButton } from './FavoriteButton';
import { DESIGN_TOKENS } from '../../styles/theme';
import type { Listing, ListingDedication } from '../../types';
import { formatDatePolish, getGoogleMapsUrl } from '../../utils';
import { useI18n } from '../../i18n';

// ============================================================
// LISTING CARD COMPONENT
// ============================================================

interface ListingCardProps {
  listing: Listing;
}

export function ListingCard({ listing }: ListingCardProps) {
  const { t } = useI18n();
  const navigate = useNavigate();

  if (!listing || !listing.basic_information) {
    return null;
  }

  const { basic_information, listing_status, permissions, order_value, Dedicated, pilots_quantity } = listing;

  const dedicationLabel: Record<ListingDedication, string | null> = {
    NONE: null,
    WORKERS_ONLY: t('listings.workersOnly'),
    FOR_VERIFIED_COOLLABOR: t('common.verified'),
  };

  const handleCardClick = () => {
    navigate(`/listings/${listing.documentId}`);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        cursor: 'pointer',
        backgroundColor: 'background.paper',
        border: `1px solid ${DESIGN_TOKENS.colors.border}`,
        boxShadow: DESIGN_TOKENS.shadows.card,
        userSelect: 'none',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: DESIGN_TOKENS.shadows.cardHover,
        },
      }}
    >
      <CardContent sx={{ flex: 1, p: 3 }}>
        {/* Status row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <ListingStatusChip status={listing_status} />
            {dedicationLabel[Dedicated] && (
              <Chip
                label={dedicationLabel[Dedicated]}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.68rem' }}
              />
            )}
            {basic_information.route_geojson && (
              <Chip
                icon={<MapIcon sx={{ fontSize: '0.875rem !important' }} />}
                label={t('listings.detailedRoute')}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.68rem', fontWeight: 600 }}
              />
            )}
            <Chip
              label={`${t('listings.pilots')}: ${pilots_quantity ?? 1}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.68rem' }}
            />
          </Box>
          <Box sx={{ position: 'absolute', top: -8, right: -8, zIndex: 10 }}>
            <FavoriteButton listingDocumentId={listing.documentId} size="small" />
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{
            mb: 1,
            lineHeight: 1.3,
            userSelect: 'text',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '3.38rem', // ~2 lines * 1.3 line-height * 1.3rem (h5 size)
          }}
        >
          {basic_information.title}
        </Typography>

        {/* Description */}
        {/* <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            userSelect: 'text',
          }}
        >
          {basic_information.description}
        </Typography> */}

        {/* Route */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            p: 1.5,
            backgroundColor: (basic_information.position_start || basic_information.end_position) ? DESIGN_TOKENS.colors.primaryLighter : 'transparent',
            borderRadius: '8px',
            minHeight: '3.25rem',
          }}
        >
          {(basic_information.position_start || basic_information.end_position) ? (
            <>
              <LocationOnIcon sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.primary, flexShrink: 0 }} />
              <Typography
                variant="caption"
                fontWeight={500}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {basic_information.position_start?.city ?? basic_information.position_start?.address ?? t('listings.startPlace')}
                {' → '}
                {basic_information.end_position?.city ?? basic_information.end_position?.address ?? t('listings.endPlace')}
              </Typography>
              {basic_information.position_start?.lat && basic_information.end_position?.lat && (
                <Link
                  component="button"
                  variant="caption"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(getGoogleMapsUrl(basic_information.position_start, basic_information.end_position), '_blank');
                  }}
                  sx={{
                    ml: 'auto',
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.primary,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.65rem',
                    flexShrink: 0
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: '14px' }} />{t('listings.map')}</Link>
              )}
            </>
          ) : (
            <Box sx={{ height: '1.25rem' }} /> // Empty placeholder line
          )}
        </Box>

        {/* Date range */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minHeight: '1.25rem' }}>
          {basic_information.time_frame ? (
            <>
              <CalendarTodayIcon sx={{ fontSize: 14, color: DESIGN_TOKENS.colors.primary }} />
              <Typography variant="caption" color="text.secondary">
                {formatDatePolish(basic_information.time_frame.startDate)}
                {basic_information.time_frame.endDate && ` – ${formatDatePolish(basic_information.time_frame.endDate)}`}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" sx={{ visibility: 'hidden' }}>-</Typography>
          )}
        </Box>

        {/* Permissions */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5, minHeight: '1.375rem' }}>
          {permissions.length > 0 ? (
            permissions.map((permission) => (
              <PermissionChip key={permission} permission={permission} />
            ))
          ) : (
            <Typography variant="caption" sx={{ visibility: 'hidden' }}>-</Typography>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Order value */}
        <Box sx={{ minHeight: '2.5rem', display: 'flex', alignItems: 'flex-start' }}>
          {order_value !== undefined && order_value !== null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <EuroIcon sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.accent }} />
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {order_value.toLocaleString('pl-PL')} PLN
              </Typography>
              <Typography variant="caption" color="text.secondary">{t('listings.netValue')}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <EuroIcon sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.accent, mt: 0.3 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.2 }}>{t('listings.amountNotProvided')}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================
// LISTINGS SKELETON
// ============================================================

export function ListingCardSkeleton() {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Skeleton variant="rounded" width={80} height={22} sx={{ mb: 1.5 }} />
        <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} width="70%" sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={36} sx={{ mb: 1.5 }} />
        <Skeleton variant="text" height={20} width="50%" />
      </CardContent>
    </Card>
  );
}
